import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { FileUp, Trash2, Loader2, Save, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { API } from "../context/ShopContext";

const fmtSize = (bytes) => {
  if (!bytes) return "";
  if (bytes > 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
};

const ProductFileRow = ({ product, authHeaders, onChanged }) => {
  const fileRef = useRef(null);
  const [maxDl, setMaxDl] = useState(product.digital_file?.max_downloads ?? 5);
  const [busy, setBusy] = useState(false);

  const isDigital = !!product.is_digital;

  const toggleDigital = async (checked) => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/products/${product.id}/digital`, { is_digital: checked }, authHeaders());
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("max_downloads", String(maxDl));
      await axios.post(`${API}/admin/products/${product.id}/file`, fd, authHeaders());
      toast.success(`File caricato per ${product.name.it}`);
      onChanged();
    } catch (err) {
      toast.error("Errore upload: " + (err.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/products/${product.id}/file-settings`, { max_downloads: Number(maxDl) }, authHeaders());
      toast.success("Limite aggiornato");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const removeFile = async () => {
    if (!window.confirm(`Rimuovere il file di "${product.name.it}"?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/products/${product.id}/file`, authHeaders());
      toast.success("File rimosso");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const df = product.digital_file;
  const docRefIt = useRef(null);
  const docRefEn = useRef(null);

  const uploadDoc = async (file, lang) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("lang", lang);
      await axios.post(`${API}/admin/products/${product.id}/doc-file?lang=${lang}`, fd, authHeaders());
      toast.success(`Documentazione ${lang.toUpperCase()} caricata per ${product.name.it}`);
      onChanged();
    } catch (err) {
      toast.error("Errore upload: " + (err.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
      if (lang === "it" && docRefIt.current) docRefIt.current.value = "";
      if (lang === "en" && docRefEn.current) docRefEn.current.value = "";
    }
  };

  const removeDoc = async (lang) => {
    if (!window.confirm(`Rimuovere la documentazione ${lang.toUpperCase()} di "${product.name.it}"?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/products/${product.id}/doc-file?lang=${lang}`, authHeaders());
      toast.success(`Documentazione ${lang.toUpperCase()} rimossa`);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="idm-admin-prodfile" data-testid={`prodfile-${product.id}`}>
      <div className="idm-admin-prodfile-info">
        <strong>{product.name.it}</strong>
        {isDigital ? (
          df ? (
            <p>
              {df.filename} ({fmtSize(df.size)}) — max download: {df.max_downloads || "∞"}
            </p>
          ) : (
            <p className="idm-admin-prodfile-none">Nessun file caricato</p>
          )
        ) : (
          <p className="idm-admin-prodfile-none">Servizio — nessun file da caricare</p>
        )}
        <label className="idm-digital-toggle" data-testid={`prodfile-digital-${product.id}`}>
          <input
            type="checkbox"
            checked={isDigital}
            disabled={busy}
            onChange={(e) => toggleDigital(e.target.checked)}
            data-testid={`prodfile-digital-checkbox-${product.id}`}
          />
          Prodotto digitale (file scaricabile)
        </label>
      </div>
      {isDigital && (
        <div className="idm-admin-prodfile-actions">
          <label className="idm-admin-prodfile-max">
            Max DL
            <input
              type="number"
              min={0}
              value={maxDl}
              onChange={(e) => setMaxDl(e.target.value)}
              data-testid={`prodfile-max-${product.id}`}
            />
          </label>
          {df && (
            <button className="idm-btn-dark idm-admin-mini" onClick={saveSettings} disabled={busy} title="Salva limite" data-testid={`prodfile-save-${product.id}`}>
              <Save size={13} />
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            style={{ display: "none" }}
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid={`prodfile-input-${product.id}`}
          />
          <button className="idm-btn-yellow idm-admin-mini" style={{ animation: "none" }} onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`prodfile-upload-${product.id}`}>
            {busy ? <Loader2 size={13} className="idm-spin" /> : <FileUp size={13} />} {df ? "Sostituisci" : "Carica file"}
          </button>
          {df && (
            <button className="idm-admin-prodfile-del" onClick={removeFile} disabled={busy} data-testid={`prodfile-delete-${product.id}`}>
              <Trash2 size={14} />
            </button>
          )}
        </div>
      )}
      <div className="idm-admin-prodfile-doc" style={{ borderTop: "1px solid #eee", marginTop: 10, paddingTop: 10 }}>
        <div style={{ fontSize: 13, color: "#555", marginBottom: 8 }}>
          <BookOpen size={13} style={{ verticalAlign: -2, marginRight: 5 }} />
          <strong>Documentazione</strong>
        </div>
        {[{ lang: "it", ref: docRefIt, label: "IT" }, { lang: "en", ref: docRefEn, label: "EN" }].map(({ lang, ref, label }) => {
          const docField = lang === "it" ? product.doc_file_it : product.doc_file_en;
          return (
            <div key={lang} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: docField ? "#333" : "#aaa" }}>
                <strong>{label}:</strong> {docField ? docField.filename : "nessun file"}
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                <input ref={ref} type="file" accept=".html,.htm" style={{ display: "none" }} onChange={(e) => uploadDoc(e.target.files?.[0], lang)} data-testid={`doc-input-${product.id}-${lang}`} />
                <button className="idm-btn-dark idm-admin-mini" style={{ fontSize: 12 }} onClick={() => ref.current?.click()} disabled={busy} data-testid={`doc-upload-${product.id}-${lang}`}>
                  {busy ? <Loader2 size={13} className="idm-spin" /> : <FileUp size={13} />} {docField ? `Sostituisci ${label}` : `Carica HTML ${label}`}
                </button>
                {docField && (
                  <button className="idm-admin-prodfile-del" onClick={() => removeDoc(lang)} disabled={busy} title={`Rimuovi doc ${label}`} data-testid={`doc-delete-${product.id}-${lang}`}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ProductFilesAdmin = ({ authHeaders }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/products`, authHeaders());
      setProducts(data);
    } catch (err) {
      toast.error("Errore caricamento prodotti");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const digital = products.filter((p) => p.is_digital);
  const services = products.filter((p) => !p.is_digital);

  return (
    <section className="idm-admin-section" data-testid="admin-product-files">
      <h3 className="idm-admin-section-title">File prodotti digitali (Area Clienti)</h3>
      <p className="idm-admin-section-sub">
        Solo i prodotti contrassegnati come "digitali" richiedono un file scaricabile. I servizi
        (sviluppo, supporto) non necessitano di alcun caricamento. "Max DL" = numero massimo di
        download per ordine (0 = illimitati). Per i prodotti in abbonamento il download richiede
        inoltre un abbonamento attivo.
      </p>
      {loading ? (
        <div className="idm-loading"><span className="idm-spinner" /></div>
      ) : (
        <>
          {digital.map((p) => (
            <ProductFileRow key={p.id} product={p} authHeaders={authHeaders} onChanged={load} />
          ))}
          {services.length > 0 && (
            <p className="idm-admin-subdivider" data-testid="services-divider">Servizi (nessun file richiesto)</p>
          )}
          {services.map((p) => (
            <ProductFileRow key={p.id} product={p} authHeaders={authHeaders} onChanged={load} />
          ))}
        </>
      )}
    </section>
  );
};

export default ProductFilesAdmin;
